import { supabase } from '../lib/supabase';
import { clientService } from './clientService';

export const appointmentService = {
  /**
   * Create a new appointment with client information
   */
  async createAppointment(appointmentData) {
    try {
      console.log('appointmentService.createAppointment called with:', appointmentData);

      // STEP 0: Validate post-natal service gender requirements
      // Check if any selected service is a post-natal service
      const hasPostNatalService = appointmentData?.services?.some(
        service => service?.category === 'post-natal'
      );

      console.log('🔍 Post-natal service validation:', {
        hasPostNatalService,
        services: appointmentData?.services?.map(s => ({ name: s?.title || s?.name, category: s?.category })),
        requestedGender: appointmentData?.details?.gender
      });

      // If post-natal service is selected, check if existing patient is male
      if (hasPostNatalService) {
        console.log('⚠️ Post-natal service detected - validating gender requirements...');

        // Check if patient already exists in database
        const existingClient = await clientService?.getClientByPhone(appointmentData?.details?.phone);

        if (existingClient) {
          console.log('📋 Found existing patient:', {
            id: existingClient?.id,
            name: existingClient?.full_name,
            gender: existingClient?.gender,
            phone: existingClient?.phone
          });

          // Validate: If existing patient is Male, block the booking
          if (existingClient?.gender === 'Male') {
            console.error('❌ VALIDATION FAILED: Male patient attempting to book post-natal service');
            throw new Error('Post-natal services are only available for female patients. Please contact us at (784) 220-7799 if you have questions.');
          }

          console.log('✅ Gender validation passed - existing female patient can book post-natal service');
        } else {
          console.log('✅ New patient - gender validation will be enforced by UI (Female pre-selected)');

          // Additional safety check: Ensure new patient has Female gender for post-natal services
          if (appointmentData?.details?.gender !== 'Female') {
            console.error('❌ VALIDATION FAILED: Post-natal service requires Female gender');
            throw new Error('Post-natal services are only available for female patients.');
          }
        }
      }

      // Step 1: Get or create client record
      const clientInfo = {
        name: appointmentData?.details?.name,
        fullName: appointmentData?.details?.name,
        email: appointmentData?.details?.email,
        phone: appointmentData?.details?.phone,
        gender: appointmentData?.details?.gender,
        chiefComplaint: appointmentData?.details?.chiefComplaint,
        medicalHistory: appointmentData?.details?.medicalHistory,
        currentMedications: appointmentData?.details?.currentMedications,
        consultationMode: appointmentData?.details?.consultationMode,
        reminderPreferences: appointmentData?.details?.reminderPreferences,
        source: 'website'
      };

      console.log('Creating/fetching client with info:', clientInfo);
      const client = await clientService?.getOrCreateClient(clientInfo, hasPostNatalService);
      console.log('Client result:', client);

      if (!client) {
        throw new Error('Failed to create or retrieve client record');
      }

      // Step 2: Create appointment record
      // Three scenarios:
      // 1. Doctor consultation: doctor_id is set, therapist_ids is empty array
      // 2. Treatment with therapist(s): therapist_ids contains staff IDs, doctor_id can be NULL
      // 3. Treatment without therapist: therapist_ids is empty array (therapists assigned later by admin)
      // CRITICAL: Build appointment record WITHOUT any 'id' field
      // If an 'id' is present, Supabase will perform an UPDATE instead of INSERT

      // Prepare therapist_ids as JSONB array
      let therapistIds = [];
      if (appointmentData?.therapist?.id) {
        // Single therapist (legacy support)
        therapistIds = [{ staff_id: appointmentData.therapist.id, role: 'primary' }];
      } else if (appointmentData?.therapists && Array.isArray(appointmentData.therapists)) {
        // Multiple therapists
        therapistIds = appointmentData.therapists.map((therapist, index) => ({
          staff_id: therapist.id || therapist.staff_id || therapist.staffId,
          role: index === 0 ? 'primary' : 'assistant'
        }));
      }

      // Determine booking_type based on appointment data
      // - 'consultation': Doctor consultations (has doctor)
      // - 'service': Wellness services from Service Universe (has services array)
      // - 'treatment': Prescribed medical treatments (has treatmentId but no services)
      // PRIORITY: Use bookingType from appointmentData if explicitly provided (from admin booking modal)
      let bookingType = appointmentData?.bookingType || 'treatment'; // Use provided bookingType or default fallback

      // If not explicitly provided, determine from appointment data structure
      if (!appointmentData?.bookingType) {
        if (appointmentData?.doctor) {
          bookingType = 'consultation';
        } else if (appointmentData?.services && appointmentData?.services?.length > 0) {
          bookingType = 'service';
        } else if (appointmentData?.treatmentId) {
          bookingType = 'treatment';
        }
      }

      console.log('🏷️ Booking type determined:', bookingType, {
        providedBookingType: appointmentData?.bookingType,
        hasDoctor: !!appointmentData?.doctor,
        hasServices: !!(appointmentData?.services && appointmentData?.services?.length > 0),
        hasTreatmentId: !!appointmentData?.treatmentId
      });

      // Determine appointment status based on payment status
      // - 'confirmed': Payment completed (online payment successful)
      // - 'scheduled': Payment pending (pay at clinic)
      const paymentStatus = appointmentData?.payment?.method === 'clinic' ? 'pending' : 'paid';
      const appointmentStatus = paymentStatus === 'paid' ? 'confirmed' : 'scheduled';

      console.log('📊 Appointment status logic:', {
        paymentMethod: appointmentData?.payment?.method,
        paymentStatus: paymentStatus,
        appointmentStatus: appointmentStatus,
        reasoning: paymentStatus === 'paid'
          ? 'Payment completed → Status: confirmed'
          : 'Payment pending → Status: scheduled'
      });

      // Prepare service_ids for wellness services
      let serviceIds = null;
      if (appointmentData?.services && Array.isArray(appointmentData.services) && appointmentData.services.length > 0) {
        serviceIds = appointmentData.services.map(service => ({
          service_id: service.id,
          name: service.name || service.title,
          base_price: service.base_price,
          category: service.category
        }));
        console.log('💆 Storing wellness services:', serviceIds);
      }

      const appointmentRecord = {
        client_id: client?.id,
        // Doctor consultation: doctor.userId is the user_profiles.id
        doctor_id: appointmentData?.doctor?.userId || null,
        // Treatment booking: therapist_ids is JSONB array of staff assignments
        therapist_ids: therapistIds,
        // Wellness services: service_ids is JSONB array of selected services
        service_ids: serviceIds,
        treatment_id: appointmentData?.treatmentId || null,
        preferred_date: appointmentData?.dateTime?.date,
        preferred_time: appointmentData?.dateTime?.timeSlot,
        status: appointmentStatus, // 'confirmed' if paid, 'scheduled' if pending
        payment_status: paymentStatus, // 'paid' or 'pending'
        payment_method: appointmentData?.payment?.method,
        fee: appointmentData?.doctor?.consultationFee || appointmentData?.treatmentFee || 0,
        chief_complaint: appointmentData?.details?.chiefComplaint,
        medical_history: appointmentData?.details?.medicalHistory,
        current_medications: appointmentData?.details?.currentMedications,
        severity: appointmentData?.details?.severity,
        consultation_mode: appointmentData?.details?.consultationMode,
        notes: appointmentData?.notes || '',
        whatsapp_notification_sent: false,
        booking_type: bookingType,
        // Razorpay payment tracking (for online payments)
        razorpay_payment_id: appointmentData?.payment?.razorpay_payment_id || null,
        razorpay_order_id: appointmentData?.payment?.razorpay_order_id || null,
        razorpay_signature: appointmentData?.payment?.razorpay_signature || null,
        payment_timestamp: paymentStatus === 'paid' ? new Date().toISOString() : null
      };

      console.log('💳 Payment tracking details:', {
        razorpay_payment_id: appointmentRecord.razorpay_payment_id,
        razorpay_order_id: appointmentRecord.razorpay_order_id,
        payment_timestamp: appointmentRecord.payment_timestamp,
        hasPaymentData: !!(appointmentData?.payment?.razorpay_payment_id)
      });

      // SAFETY CHECK: Ensure no 'id' field is present (would cause UPDATE instead of INSERT)
      if (appointmentRecord.id) {
        console.error('🚨 CRITICAL ERROR: appointmentRecord contains an "id" field!');
        console.error('🚨 This will cause an UPDATE instead of INSERT!');
        console.error('🚨 Removing the id field to force INSERT...');
        delete appointmentRecord.id;
      }

      // SAFETY CHECK: Verify appointmentData doesn't contain an id
      if (appointmentData?.id || appointmentData?.appointmentId) {
        console.error('🚨 WARNING: appointmentData contains an id field:', {
          id: appointmentData?.id,
          appointmentId: appointmentData?.appointmentId
        });
        console.error('🚨 This should NOT be passed to createAppointment!');
      }

      console.log('═══════════════════════════════════════════════════════════');
      console.log('🔵 APPOINTMENT INSERT OPERATION STARTING');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('📝 Appointment Record to INSERT:', JSON.stringify(appointmentRecord, null, 2));
      console.log('🔍 Checking for "id" field in record:', appointmentRecord.hasOwnProperty('id') ? '❌ FOUND (BAD!)' : '✅ NOT FOUND (GOOD!)');
      console.log('⚠️  IMPORTANT: This is an INSERT operation, NOT an UPDATE');
      console.log('⚠️  Each booking creates a NEW appointment record with a UNIQUE ID');
      console.log('⚠️  The client_id will be REUSED if the same phone number books again');
      console.log('⚠️  But the appointment ID will ALWAYS be NEW and UNIQUE');
      console.log('═══════════════════════════════════════════════════════════');

      // Insert without complex joins - just get the basic appointment data back
      // This is ALWAYS an INSERT operation - never an UPDATE
      // Each booking creates a new appointment record with a unique ID
      // IMPORTANT: We're using .insert() NOT .upsert()
      console.log('🔧 Supabase operation: .insert() on "appointments" table');
      console.log('🔧 This will create a NEW row with a NEW UUID');

      const { data: appointment, error: appointmentError } = await supabase
        ?.from('appointments')
        ?.insert(appointmentRecord)
        ?.select('*')
        ?.single();

      console.log('🔧 Supabase response received');
      console.log('🔧 Response data:', appointment);
      console.log('🔧 Response error:', appointmentError);

      if (appointmentError) {
        console.error('Error creating appointment:', appointmentError);
        console.error('Appointment error details:', JSON.stringify(appointmentError, null, 2));
        throw appointmentError;
      }

      console.log('═══════════════════════════════════════════════════════════');
      console.log('✅ SUCCESS: NEW APPOINTMENT CREATED!');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('🆕 NEW Appointment ID:', appointment?.id);
      console.log('📅 Appointment created_at:', appointment?.created_at);
      console.log('🔄 Appointment updated_at:', appointment?.updated_at);
      console.log('📊 Appointment Status:', appointment?.status);
      console.log('📍 Appointment Date:', appointment?.preferred_date);
      console.log('⏰ Appointment Time:', appointment?.preferred_time);
      console.log('───────────────────────────────────────────────────────────');
      console.log('👤 Client Information (MAY BE REUSED FROM PREVIOUS BOOKING):');
      console.log('🆔 Client ID:', client?.id);
      console.log('📅 Client created_at:', client?.created_at, '← This may be OLD if client exists');
      console.log('📧 Client email:', client?.email, '← From previous booking if exists');
      console.log('📱 Client phone:', client?.phone);
      console.log('👤 Client name:', client?.full_name);
      console.log('───────────────────────────────────────────────────────────');
      console.log('🎯 VERIFICATION CHECKLIST:');
      console.log('   ✓ Appointment ID should be UNIQUE (different from previous bookings)');
      console.log('   ✓ Appointment created_at should be CURRENT timestamp');
      console.log('   ✓ Client ID may be SAME as previous booking (if same phone number)');
      console.log('   ✓ Client created_at may be OLD (if client already existed)');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('Full appointment object:', JSON.stringify(appointment, null, 2));
      console.log('═══════════════════════════════════════════════════════════');

      // Step 3: Update client's appointment count
      try {
        await clientService?.updateClientAppointmentCount(client?.id);
      } catch (countError) {
        console.warn('Failed to update client appointment count:', countError);
        // Don't throw - this is not critical
      }

      return {
        appointment,
        client
      };
    } catch (error) {
      console.error('Error in createAppointment:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      throw error;
    }
  },

  /**
   * Send WhatsApp notification for appointment
   * Sends messages to both CLIENT and ADMIN via AiSensy WhatsApp Business API
   */
  async sendWhatsAppNotification(appointmentId, appointmentDetails) {
    try {
      console.log('📱 Attempting to send WhatsApp notifications...');
      console.log('Appointment ID:', appointmentId);
      console.log('Client phone:', appointmentDetails?.client?.phone);

      // Check if whatsapp_messages table exists
      const { error: tableCheckError } = await supabase
        ?.from('whatsapp_messages')
        ?.select('id')
        ?.limit(1);

      if (tableCheckError) {
        if (tableCheckError?.code === 'PGRST205' || tableCheckError?.code === '42P01') {
          console.warn('⚠️ WhatsApp messages table does not exist. Skipping WhatsApp notification.');
          console.warn('💡 To enable WhatsApp notifications, run the migration: supabase/migrations/20251223123800_add_whatsapp_integration.sql');
          return null;
        }
      }

      // Format messages for client and admin
      const clientMessage = this.formatWhatsAppMessage(appointmentDetails);
      const adminMessage = this.formatAdminWhatsAppMessage(appointmentDetails);

      // Admin phone number (from environment or default)
      const ADMIN_PHONE = '7842202255'; // Clinic admin phone number

      // 1. Send message to CLIENT
      console.log('📱 Sending confirmation to client...');
      const clientResult = await this.sendWhatsAppViaAiSensy({
        appointmentId,
        phone: appointmentDetails?.client?.phone,
        message: clientMessage,
        userName: appointmentDetails?.client?.name,
        messageType: 'appointment_confirmation',
        campaignName: 'appointment_confirmation'
      });

      // 2. Send message to ADMIN
      console.log('📱 Sending notification to admin...');
      const adminResult = await this.sendWhatsAppViaAiSensy({
        appointmentId,
        phone: ADMIN_PHONE,
        message: adminMessage,
        userName: 'Admin',
        messageType: 'admin_notification',
        campaignName: 'admin_appointment_notification'
      });

      console.log('✅ WhatsApp notifications sent:', {
        client: clientResult?.success ? 'sent' : 'failed',
        admin: adminResult?.success ? 'sent' : 'failed'
      });

      return {
        client: clientResult,
        admin: adminResult
      };
    } catch (error) {
      console.error('❌ Exception in sendWhatsAppNotification:', error);
      // Don't throw error - notification failure shouldn't block appointment creation
      return null;
    }
  },

  /**
   * Send WhatsApp message via Meta WhatsApp Business API Edge Function
   */
  async sendWhatsAppViaAiSensy({ appointmentId, phone, message, userName, messageType, campaignName }) {
    try {
      // Queue message in database first
      const insertData = {
        recipient_id: null,
        phone_number: phone,
        message_content: message,
        status: 'pending',
        appointment_id: appointmentId,
        message_type: messageType
      };

      const { data: queuedMessage, error: queueError } = await supabase
        ?.from('whatsapp_messages')
        ?.insert(insertData)
        ?.select()
        ?.single();

      if (queueError) {
        console.error('❌ Error queuing WhatsApp message:', queueError);
        // Don't fail the booking if WhatsApp queueing fails
        return { success: false, error: queueError, messageQueued: false };
      }

      console.log('✅ WhatsApp message queued:', queuedMessage?.id);

      // Send via Meta WhatsApp Edge Function
      const { data: sendResult, error: sendError } = await supabase?.functions?.invoke('send-whatsapp', {
        body: {
          messageId: queuedMessage?.id,
          to: phone,
          message: message,
          userName: userName,
          // For Meta WhatsApp, we'll use text messages for now
          // In production, you should use approved templates
          // templateName: 'appointment_confirmation',
          // templateParams: [...]
        }
      });

      if (sendError) {
        console.error('❌ Error calling send-whatsapp function:', sendError);
        // Message is queued, can be sent manually later
        return { success: false, error: sendError, messageQueued: true };
      }

      if (sendResult?.success) {
        console.log('✅ WhatsApp message sent successfully via Meta WhatsApp');
        return { success: true, data: sendResult };
      } else {
        console.warn('⚠️ WhatsApp send function returned error:', sendResult?.error);
        // Check if it's a Meta API error
        if (sendResult?.error?.includes('not configured')) {
          console.warn('⚠️ Meta WhatsApp API not configured. Message queued for manual sending.');
        }
        // Message is queued, can be sent manually later
        return { success: false, error: sendResult?.error, messageQueued: true };
      }
    } catch (error) {
      console.error('❌ Exception in sendWhatsAppViaAiSensy:', error);
      return { success: false, error: error, messageQueued: false };
    }
  },

  /**
   * Format WhatsApp message for appointment confirmation
   */
  formatWhatsAppMessage(appointmentDetails) {
    const { client, doctor, dateTime, treatment, consultationFee } = appointmentDetails;

    const date = new Date(dateTime?.date);
    const formattedDate = date?.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `🌿 *Ayur Parishkar Clinic - Appointment Confirmation* 🌿

Dear ${client?.full_name || client?.name},

Your consultation has been successfully booked!

📋 *Appointment Details:*
👨‍⚕️ Doctor: Dr. ${doctor?.fullName}
📅 Date: ${formattedDate}
🕐 Time: ${dateTime?.timeSlot}
${treatment?.title ? `💊 Treatment: ${treatment?.title}` : ''}
💰 Consultation Fee: ₹${consultationFee}

📍 *Clinic Address:*
Ayur Parishkar Ayurvedic Clinic
[Your clinic address here]

⚠️ *Important Notes:*
• Please arrive 15 minutes early
• Bring any previous medical records
• Wear comfortable clothing
• Cancellations must be made 24 hours in advance

For any queries, please call: [Your clinic phone]

Thank you for choosing Ayur Parishkar! 🙏

_This is an automated message. Please do not reply._`;
  },

  /**
   * Format WhatsApp message for admin notification
   */
  formatAdminWhatsAppMessage(appointmentDetails) {
    const { client, doctor, dateTime, treatment, consultationFee } = appointmentDetails;

    const date = new Date(dateTime?.date);
    const formattedDate = date?.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `🔔 *New Appointment Booked* 🔔

📋 *Appointment Details:*
👤 Patient: ${client?.full_name || client?.name}
📞 Phone: ${client?.phone}
📧 Email: ${client?.email || 'Not provided'}

👨‍⚕️ Doctor: Dr. ${doctor?.fullName}
📅 Date: ${formattedDate}
🕐 Time: ${dateTime?.timeSlot}
${treatment?.title ? `💊 Treatment: ${treatment?.title}` : ''}
💰 Consultation Fee: ₹${consultationFee}

🌐 Booked via: Website
⏰ Booking Time: ${new Date().toLocaleString('en-IN')}

Please confirm the appointment and prepare accordingly.

_This is an automated message. Please do not reply._`;
  },

  /**
   * Get appointment by ID
   * Note: therapist_ids is now a JSONB array, so we can't use direct foreign key joins
   * We'll fetch therapist details separately
   */
  async getAppointmentById(appointmentId) {
    const { data, error } = await supabase
      ?.from('appointments')
      ?.select(`
        *,
        client:client_id(full_name, phone, email),
        doctor:user_profiles!appointments_doctor_id_fkey(full_name, email, phone),
        treatment:treatment_id(title, description)
      `)
      ?.eq('id', appointmentId)
      ?.single();

    if (error) {
      console.error('Error fetching appointment:', error);
      throw error;
    }

    // Fetch therapist details if therapist_ids exists
    if (data?.therapist_ids && Array.isArray(data.therapist_ids) && data.therapist_ids.length > 0) {
      const therapistIds = data.therapist_ids.map(t => t.staff_id);

      const { data: therapists, error: therapistError } = await supabase
        ?.from('staff')
        ?.select(`
          id,
          user_id,
          role,
          gender,
          user_profiles!staff_user_id_fkey(full_name, email, phone)
        `)
        ?.in('id', therapistIds);

      if (!therapistError && therapists) {
        // Attach therapist details to appointment
        data.therapists = therapists;
      }
    }

    return data;
  },

  /**
   * Update appointment status
   */
  async updateAppointmentStatus(appointmentId, status) {
    const { data, error } = await supabase
      ?.from('appointments')
      ?.update({ status, updated_at: new Date().toISOString() })
      ?.eq('id', appointmentId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error updating appointment status:', error);
      throw error;
    }

    return data;
  }
};

